/**
 * Chain Context
 *
 * Manages the currently selected EVM network and provides chain-switching
 * functionality and network configuration.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { DOGEOS_CHIKYU, EVM_CONFIG } from "../config";

/**
 * Supported chain types
 */
export type ChainType = "evm";

/**
 * Chain configuration
 */
export interface ChainConfig {
  id: number | string;
  name: string;
  type: ChainType;
  rpcUrl: string;
  explorerUrl: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  contracts?: {
    shieldedPool?: string;
    shieldedPoolNova?: string;
    [key: string]: string | undefined;
  };
  requireNovaVerify?: boolean; // Optional client-side toggle
}

/**
 * Predefined chain configurations
 */
export const CHAINS: Record<string, ChainConfig> = {
  // EVM Chains
  "84532": {
    id: 84532,
    name: "Base Sepolia",
    type: "evm",
    rpcUrl: "https://sepolia.base.org",
    explorerUrl: "https://sepolia.basescan.org",
    nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
    contracts: {
      shieldedPool: "0xD3fD721f22905f4daC5936fC7AB1491E07b09dAA",
      shieldedPoolNova: "0x265b97CA7E86f1cf5c282b923E100205C0Fdf24b",
    },
  },
  "8453": {
    id: 8453,
    name: "Base",
    type: "evm",
    rpcUrl: "https://mainnet.base.org",
    explorerUrl: "https://basescan.org",
    nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
  },
  "6281971": {
    id: 6281971,
    name: "DogeOS Chikyu",
    type: "evm",
    rpcUrl: "https://rpc.testnet.dogeos.com",
    explorerUrl:
      DOGEOS_CHIKYU?.explorerUrl ?? "https://blockscout.testnet.dogeos.com",
    nativeCurrency: { name: "Dogecoin", symbol: "DOGE", decimals: 18 },
  },
  "1": {
    id: 1,
    name: "Ethereum",
    type: "evm",
    rpcUrl:
      process.env.REACT_APP_ETHEREUM_RPC_URL || "https://eth.llamarpc.com",
    explorerUrl: "https://etherscan.io",
    nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
  },
};

/**
 * Chain context value
 */
interface ChainContextValue {
  // Current chain
  chainType: ChainType;
  chainId: number | string;
  chainConfig: ChainConfig | null;

  // Available chains
  availableChains: ChainConfig[];

  // Actions
  setChain: (type: ChainType, id: number | string) => void;
  switchToEVM: (chainId: number) => void;

  // Helpers
  isEVM: boolean;
  getExplorerTxUrl: (txHash: string) => string;
}

const ChainContext = createContext<ChainContextValue | null>(null);

/**
 * Chain Provider Props
 */
interface ChainProviderProps {
  children: ReactNode;
  defaultChainType?: ChainType;
  defaultChainId?: number | string;
}

/**
 * Chain Provider Component
 */
export function ChainProvider({
  children,
  defaultChainType = "evm",
  defaultChainId = Number(process.env.REACT_APP_CHAIN_ID || 8453),
}: ChainProviderProps) {
  const [chainType, setChainType] = useState<ChainType>(defaultChainType);
  const [chainId, setChainId] = useState<number | string>(defaultChainId);

  // Load saved chain preference from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname.replace(/\/+$/, "") || "/";
      // An EVM app route is pinned to the network the page serves.
      if (path.startsWith("/base") || path.startsWith("/dogeos")) {
        setChainType("evm");
        setChainId(EVM_CONFIG.CHAIN_ID);
        return;
      }
    }
    const saved = localStorage.getItem("stealthos-chain");
    if (saved) {
      try {
        const { type, id } = JSON.parse(saved);
        setChainType(type);
        setChainId(id);
      } catch {
        // Invalid saved data, use defaults
      }
    }
  }, []);

  // Save chain preference
  useEffect(() => {
    localStorage.setItem(
      "stealthos-chain",
      JSON.stringify({ type: chainType, id: chainId }),
    );
  }, [chainType, chainId]);

  // Get current chain config
  const chainConfig = React.useMemo(() => {
    return CHAINS[String(chainId)] || null;
  }, [chainId]);

  // Get available chains
  const availableChains = React.useMemo(() => {
    return Object.values(CHAINS);
  }, []);

  // Set chain
  const setChain = (type: ChainType, id: number | string) => {
    setChainType(type);
    setChainId(id);
  };

  // Switch to EVM
  const switchToEVM = (newChainId: number) => {
    setChainType("evm");
    setChainId(newChainId);
  };

  // Get explorer URL for transaction
  const getExplorerTxUrl = (txHash: string): string => {
    if (!chainConfig) return "";

    return `${chainConfig.explorerUrl}/tx/${txHash}`;
  };

  const value: ChainContextValue = {
    chainType,
    chainId,
    chainConfig,
    availableChains,
    setChain,
    switchToEVM,
    isEVM: chainType === "evm",
    getExplorerTxUrl,
  };

  return (
    <ChainContext.Provider value={value}>{children}</ChainContext.Provider>
  );
}

/**
 * Hook to use chain context
 */
export function useChain(): ChainContextValue {
  const context = useContext(ChainContext);
  if (!context) {
    throw new Error("useChain must be used within a ChainProvider");
  }
  return context;
}

/**
 * Hook to check if connected to specific chain type
 */
export function useIsChainType(type: ChainType): boolean {
  const { chainType } = useChain();
  return chainType === type;
}

export default ChainContext;
